import type { OperationResult } from '@urql/vue';
import type { ComputedRef } from 'vue';

import { isServer } from '~/utils/runtime';
import { truncateMetaDescription } from '~/utils/seo';

interface UsePublicResourcePageOptions<TData, TRow> {
  /**
   * useAsyncData cache key — unique per resource. Pass a getter (`() => `lac:${slug.value}``)
   * so param-only route changes (which reuse the page component) refetch under the correct key
   * rather than storing the new slug's data under the old key.
   */
  key: string | (() => string);
  /** Fires the public GraphQL query and resolves the urql result. */
  query: () => Promise<OperationResult<TData>>;
  /** Pulls the single row out of the query payload (null when absent). */
  selectRow: (data: TData | null) => TRow | null;
  /** SEO title / og:title source. */
  name: (row: TRow) => string | null | undefined;
  /** Rich-text-flattened meta description source (truncated to 160 chars). */
  description?: (row: TRow) => string | null | undefined;
  /** og:image source. */
  image?: (row: TRow) => string | null | undefined;
  /** statusMessage for the server-side 404 when the row is absent. */
  notFound: string;
}

interface ResourcePageMeta {
  title: () => string | undefined;
  description: () => string | undefined;
  image: () => string | undefined;
}

/**
 * Server-render a public detail page for a single resource: await the public query so the
 * graphcache + ssrExchange are primed before render, throw a 404 server-side when the resource
 * genuinely doesn't exist, and assemble its SEO meta. Renders the anonymous/public view;
 * personalization hydrates client-side. (Runs on SPA navigations too, hence no "ssr" in the
 * name — it pairs with the sibling `usePublicActivityPage` these pages also call.)
 *
 * A hard failure (an error with no data) is surfaced as the real error (→ 500, logged
 * server-side) rather than being swallowed into a null result that masquerades as a 404; a
 * partial field-level error alongside data still renders. Reserving 404 for a genuinely
 * empty payload keeps valid pages indexable.
 */
export const usePublicResourcePage = async <TData, TRow>(
  options: UsePublicResourcePageOptions<TData, TRow>,
): Promise<{ row: ComputedRef<TRow | null>; meta: ResourcePageMeta }> => {
  // Capture the Nuxt app before the await below: useAsyncData's await runs inside this nested
  // async function (not the page's own setup), so Vue does not restore the active instance
  // afterwards. Composables that need the Nuxt instance (useSeoMeta) must therefore be invoked
  // through nuxtApp.runWithContext to avoid "[nuxt] instance unavailable".
  const nuxtApp = useNuxtApp();
  const { data, error } = await useAsyncData(
    options.key,
    async () => {
      const result = await options.query();
      // A hard failure (error with no usable data) throws; useAsyncData catches it into
      // error.value, which we rethrow below. A partial, field-level error alongside data is
      // expected here (e.g. per-user fields resolving for an unauthenticated SSR request), so
      // we return the data we did get and render it.
      if (result.error && !result.data) {
        throw result.error;
      }
      return result.data ?? null;
    },
  );

  // useAsyncData never rejects — a handler throw lands in error.value with data left null. If
  // we didn't rethrow here, a transient backend failure would fall through to the 404 below and
  // deindex a valid page. Surface it as the real error (→ 500) instead.
  if (error.value) {
    throw error.value;
  }

  // useAsyncData re-types the payload through its PickFrom<> transform; cast back to the
  // caller's TData so selectRow keeps its precise input type.
  const row = computed(() => options.selectRow((data.value ?? null) as TData | null));

  if (isServer && !row.value) {
    throw createError({ statusCode: 404, statusMessage: options.notFound });
  }

  const title = () => (row.value ? options.name(row.value) ?? undefined : undefined);
  const description = () => {
    if (!row.value || !options.description) {
      return undefined;
    }
    const text = options.description(row.value);
    if (!text) {
      return undefined;
    }
    return truncateMetaDescription(text);
  };
  const image = () => (row.value && options.image ? options.image(row.value) ?? undefined : undefined);

  nuxtApp.runWithContext(() => useSeoMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: image,
  }));

  return { row, meta: { title, description, image } };
};
